import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import ReportNavigationSidebar from '../../components/navigation/ReportNavigationSidebar';
import ReportHeader from './components/ReportHeader';
import ReportMetadata from './components/ReportMetadata';
import ExportControls from './components/ExportControls';

import ReportAnalysisPanel from './components/ReportAnalysisPanel';



const ReportRenderingView = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('executive-summary');

  const reportMetadata = {
    generatedAt: "2026-01-20T15:30:00Z",
    contributingAgents: ["Coordinador Principal", "Redactor PDD", "Especialista MRV", "Analista Geoespacial"],
    projectName: "Restauración Forestal Amazónica - Perú",
    version: "v2.1.0"
  };

  const reportSections = [
    {
      id: 'executive-summary',
      title: 'Resumen Ejecutivo',
      badge: '1'
    },
    {
      id: 'project-description',
      title: 'Descripción del Proyecto',
      badge: '2',
      children: [
        { id: 'project-overview', title: 'Visión General' },
        { id: 'project-location', title: 'Ubicación Geográfica' },
        { id: 'project-timeline', title: 'Cronograma' }
      ]
    },
    {
      id: 'methodology',
      title: 'Metodología',
      badge: '3',
      children: [
        { id: 'baseline-scenario', title: 'Escenario de Línea Base' },
        { id: 'project-scenario', title: 'Escenario del Proyecto' },
        { id: 'additionality', title: 'Adicionalidad' }
      ]
    },
    {
      id: 'mrv-plan',
      title: 'Plan MRV',
      badge: '4',
      children: [
        { id: 'monitoring', title: 'Monitoreo' },
        { id: 'reporting', title: 'Reporte' },
        { id: 'verification', title: 'Verificación' }
      ]
    },
    {
      id: 'carbon-accounting',
      title: 'Contabilidad de Carbono',
      badge: '5'
    },
    {
      id: 'safeguards',
      title: 'Salvaguardas',
      badge: '6',
      children: [
        { id: 'environmental', title: 'Ambientales' },
        { id: 'social', title: 'Sociales' }
      ]
    },
    {
      id: 'gee-analysis',
      title: 'Análisis Geoespacial (GEE)',
      badge: '7'
    },
    {
      id: 'annexes',
      title: 'Anexos',
      badge: '8'
    }
  ];

  const finalReportContent = `# Informe Técnico de Proyecto AFOLU

## 1. Resumen Ejecutivo

El proyecto de **Restauración Forestal Amazónica** en Perú representa una iniciativa de forestación y reforestación (A/R) bajo el estándar Verra VCS. El proyecto abarca 15,000 hectáreas de tierras degradadas en la región de Ucayali, con un potencial de captura de **450,000 tCO₂e** durante el período de acreditación de 30 años.

### Objetivos Principales

- Restaurar ecosistemas forestales degradados mediante especies nativas
- Generar créditos de carbono verificables bajo metodología VM0006
- Crear oportunidades de empleo para 250 familias locales
- Proteger la biodiversidad amazónica y servicios ecosistémicos

### Indicadores Clave de Desempeño

| Indicador | Valor Proyectado | Unidad |
|-----------|------------------|--------|
| Área Total | 15,000 | hectáreas |
| Captura Anual | 15,000 | tCO₂e/año |
| Captura Total (30 años) | 450,000 | tCO₂e |
| Densidad de Captura | 30 | tCO₂e/ha/año |
| Familias Beneficiadas | 250 | familias |

## 2. Descripción del Proyecto

### 2.1 Visión General

El proyecto implementa un modelo de restauración forestal basado en sistemas agroforestales y plantaciones de enriquecimiento. La estrategia combina especies maderables de alto valor (caoba, cedro) con especies de rápido crecimiento (bolaina, capirona) para maximizar tanto la captura de carbono como los beneficios socioeconómicos.

**Componentes del Proyecto:**

- Establecimiento de 10,000 ha de plantaciones forestales
- Restauración de 3,000 ha mediante regeneración natural asistida
- Implementación de 2,000 ha de sistemas agroforestales
- Programa de monitoreo y protección forestal

### 2.2 Ubicación Geográfica

**Coordenadas del Proyecto:**
- Latitud: -8.3791° S
- Longitud: -74.5447° W
- Región: Ucayali, Perú
- Distrito: Padre Abad

El área del proyecto se encuentra en la zona de transición entre la selva alta y baja amazónica, con precipitación anual promedio de 2,800 mm y temperatura media de 26°C.

### 2.3 Cronograma de Implementación

| Fase | Período | Actividades Principales |
|------|---------|-------------------------|
| Preparación | Año 0-1 | Diseño técnico, consultas comunitarias, validación |
| Establecimiento | Año 1-5 | Plantación, mantenimiento inicial, capacitación |
| Consolidación | Año 5-15 | Monitoreo, manejo silvicultural, verificaciones |
| Madurez | Año 15-30 | Cosecha selectiva, regeneración, verificaciones finales |

## 3. Metodología

### 3.1 Escenario de Línea Base

El escenario de línea base se define según la metodología **VM0006 v4.2** para proyectos A/R. Sin el proyecto, las tierras continuarían en estado de degradación con cobertura de pastizales y vegetación secundaria de bajo valor ecológico.

**Supuestos de Línea Base:**

- Uso de suelo: Pastizales degradados y agricultura de subsistencia
- Stock de carbono: 15 tC/ha (promedio)
- Tasa de deforestación regional: 0.8% anual
- Presión de conversión: Alta (expansión agrícola y ganadera)

### 3.2 Escenario del Proyecto

El proyecto establece plantaciones forestales y sistemas agroforestales que incrementan significativamente los stocks de carbono en biomasa aérea, subterránea, hojarasca y suelo.

**Proyecciones de Carbono:**

\`\`\`
Año 10: 120 tC/ha
Año 20: 180 tC/ha
Año 30: 220 tC/ha
\`\`\`

### 3.3 Adicionalidad

El proyecto demuestra adicionalidad mediante:

1. **Análisis de Inversión:** TIR sin créditos de carbono: 3.2% (por debajo del benchmark de 8%)
2. **Análisis de Barreras:** Falta de financiamiento inicial, riesgos técnicos, barreras institucionales
3. **Análisis de Práctica Común:** Menos del 5% del área regional bajo restauración forestal

## 4. Plan de Monitoreo, Reporte y Verificación (MRV)

### 4.1 Sistema de Monitoreo

El proyecto implementa un sistema de monitoreo robusto basado en:

- **Parcelas Permanentes:** 150 parcelas de 0.1 ha distribuidas estratificadamente
- **Mediciones de Campo:** Diámetro a la altura del pecho (DAP), altura total, especies
- **Frecuencia:** Mediciones cada 2 años durante los primeros 10 años, luego cada 5 años
- **Tecnología:** Integración de datos de campo con análisis de imágenes satelitales

### 4.2 Ecuaciones Alométricas

Para la estimación de biomasa aérea se utilizan ecuaciones específicas para bosques tropicales:

\`\`\`
AGB = 0.0673 × (ρ × DBH² × H)^0.976
\`\`\`

Donde:
- AGB = Biomasa aérea (kg)
- ρ = Densidad de madera (g/cm³)
- DBH = Diámetro a la altura del pecho (cm)
- H = Altura total (m)

### 4.3 Programa de Verificación

| Verificación | Año | Alcance |
|--------------|-----|---------|
| Validación Inicial | Año 0 | Diseño del proyecto, línea base |
| Primera Verificación | Año 5 | Primeros 5 años de implementación |
| Segunda Verificación | Año 10 | Años 6-10 |
| Verificaciones Subsecuentes | Cada 5 años | Períodos de monitoreo completos |

## 5. Contabilidad de Carbono

### 5.1 Reservorios de Carbono

El proyecto contabiliza los siguientes reservorios:

- **Biomasa Aérea:** Principal reservorio, incluye troncos, ramas, hojas
- **Biomasa Subterránea:** Raíces (estimadas como 20% de biomasa aérea)
- **Hojarasca:** Materia orgánica en descomposición
- **Suelo:** Carbono orgánico del suelo (0-30 cm de profundidad)

### 5.2 Cálculo de Reducciones de Emisiones

**Fórmula General:**

\`\`\`
ER = (C_project - C_baseline) - Leakage - Buffer
\`\`\`

Donde:
- ER = Reducciones de emisiones verificables
- C_project = Carbono en escenario del proyecto
- C_baseline = Carbono en escenario de línea base
- Leakage = Fugas (estimadas en 10%)
- Buffer = Reserva de riesgo (20% según Verra)

### 5.3 Proyección de Créditos

| Período | Captura Bruta (tCO₂e) | Fugas (10%) | Buffer (20%) | Créditos Netos |
|---------|------------------------|-------------|--------------|----------------|
| Año 1-5 | 45,000 | 4,500 | 8,100 | 32,400 |
| Año 6-10 | 75,000 | 7,500 | 13,500 | 54,000 |
| Año 11-15 | 90,000 | 9,000 | 16,200 | 64,800 |
| Año 16-30 | 240,000 | 24,000 | 43,200 | 172,800 |
| **Total** | **450,000** | **45,000** | **81,000** | **324,000** |

## 6. Salvaguardas

### 6.1 Salvaguardas Ambientales

El proyecto implementa medidas para:

- Proteger especies nativas y biodiversidad local
- Prevenir la introducción de especies invasoras
- Mantener servicios ecosistémicos (agua, suelo, polinización)
- Evitar el uso de agroquímicos peligrosos

**Indicadores de Monitoreo Ambiental:**

- Índice de diversidad de Shannon-Wiener
- Presencia de especies indicadoras
- Calidad del agua en microcuencas
- Conectividad del paisaje

### 6.2 Salvaguardas Sociales

El proyecto garantiza:

- Consentimiento libre, previo e informado de comunidades locales
- Respeto a derechos territoriales y culturales
- Distribución equitativa de beneficios
- Participación de mujeres y jóvenes en actividades del proyecto

**Mecanismo de Quejas y Reclamos:**

Sistema de atención de quejas con tres niveles:
1. Resolución comunitaria (15 días)
2. Mediación con equipo del proyecto (30 días)
3. Arbitraje independiente (60 días)

## 7. Conclusiones y Recomendaciones

### 7.1 Conclusiones Principales

1. El proyecto es técnicamente viable y cumple con los requisitos de la metodología VM0006
2. La adicionalidad está claramente demostrada mediante análisis financiero y de barreras
3. El sistema MRV propuesto es robusto y permite verificación independiente
4. Las salvaguardas ambientales y sociales están adecuadamente diseñadas

### 7.2 Recomendaciones

- Fortalecer el componente de capacitación técnica para comunidades locales
- Implementar sistema de trazabilidad digital para productos forestales
- Establecer alianzas con instituciones de investigación para monitoreo de biodiversidad
- Desarrollar estrategia de comercialización temprana de créditos de carbono

### 7.3 Próximos Pasos

1. Completar proceso de validación con organismo de validación y verificación (VVB)
2. Registrar proyecto en registro Verra
3. Iniciar actividades de establecimiento forestal
4. Implementar sistema de monitoreo digital
5. Preparar primera verificación (Año 5)`;

  const geeReportContent = `# Análisis Geoespacial con Google Earth Engine

## 1. Introducción al Análisis GEE

Este informe presenta el análisis geoespacial del proyecto utilizando **Google Earth Engine (GEE)**, una plataforma de procesamiento de imágenes satelitales a escala planetaria. El análisis se enfoca en la caracterización de la cobertura terrestre, detección de cambios y estimación de biomasa forestal.

## 2. Metodología de Análisis

### 2.1 Fuentes de Datos

| Satélite/Sensor | Resolución | Período | Uso Principal |
|-----------------|------------|---------|---------------|
| Landsat 8 OLI | 30m | 2015-2025 | Clasificación de cobertura |
| Sentinel-2 MSI | 10m | 2017-2025 | Detección de cambios |
| ALOS PALSAR | 25m | 2015-2020 | Estimación de biomasa |
| SRTM DEM | 30m | 2000 | Modelo de elevación |

### 2.2 Procesamiento de Imágenes

**Pipeline de Procesamiento:**

\`\`\`javascript
// Código GEE para análisis de cobertura
var roi = ee.Geometry.Rectangle([-74.6, -8.5, -74.4, -8.2]);

var landsat = ee.ImageCollection('LANDSAT/LC08/C02/T1_L2')
  .filterBounds(roi)
  .filterDate('2020-01-01', '2025-12-31')
  .filter(ee.Filter.lt('CLOUD_COVER', 20))
  .map(function(image) {
    return image.select(['SR_B.*']).multiply(0.0000275).add(-0.2);
  });

var composite = landsat.median().clip(roi);
\`\`\`

## 3. Resultados del Análisis

### 3.1 Clasificación de Cobertura Terrestre

**Clases de Cobertura Identificadas:**

- Bosque Primario: 35% (5,250 ha)
- Bosque Secundario: 20% (3,000 ha)
- Pastizales: 30% (4,500 ha)
- Agricultura: 10% (1,500 ha)
- Áreas Degradadas: 5% (750 ha)

### 3.2 Análisis de Cambio de Cobertura (2015-2025)

| Transición | Área (ha) | Porcentaje |
|------------|-----------|------------|
| Bosque → Pastizal | 1,200 | 8% |
| Pastizal → Agricultura | 800 | 5.3% |
| Bosque → Agricultura | 450 | 3% |
| Regeneración Natural | 350 | 2.3% |

### 3.3 Estimación de Biomasa Aérea

Utilizando datos de radar ALOS PALSAR y modelos de regresión:

**Ecuación de Biomasa:**

\`\`\`
AGB = exp(0.0556 × HV + 0.0458 × HH - 1.2345)
\`\`\`

**Resultados por Estrato:**

- Bosque Primario: 180-220 tC/ha
- Bosque Secundario: 80-120 tC/ha
- Pastizales: 5-15 tC/ha
- Áreas del Proyecto (post-restauración): 40-60 tC/ha (Año 5)

## 4. Índices de Vegetación

### 4.1 NDVI (Normalized Difference Vegetation Index)

El análisis temporal de NDVI muestra:

- Bosques: NDVI &gt; 0.7 (alta densidad de vegetación)
- Pastizales: NDVI 0.3-0.5 (vegetación moderada)
- Áreas Degradadas: NDVI &lt; 0.3 (baja cobertura vegetal)

### 4.2 EVI (Enhanced Vegetation Index)

EVI proporciona mejor sensibilidad en áreas de alta biomasa:

\`\`\`
EVI = 2.5 × (NIR - RED) / (NIR + 6×RED - 7.5×BLUE + 1)
\`\`\`

## 5. Detección de Deforestación

### 5.1 Algoritmo de Detección

Implementación de algoritmo CCDC (Continuous Change Detection and Classification):

- Detección de cambios abruptos en series temporales
- Identificación de patrones de deforestación
- Alertas tempranas de pérdida de cobertura forestal

### 5.2 Hotspots de Deforestación

**Áreas de Mayor Riesgo:**

1. Zona Norte del proyecto: 15 ha/año (presión agrícola)
2. Corredor Este: 8 ha/año (expansión ganadera)
3. Límite Sur: 5 ha/año (extracción maderera)

## 6. Análisis de Fragmentación

### 6.1 Métricas de Paisaje

| Métrica | Valor Actual | Valor Objetivo (Año 10) |
|---------|--------------|-------------------------|
| Área de Parche Promedio | 12 ha | 25 ha |
| Índice de Forma | 1.8 | 1.4 |
| Conectividad | 0.45 | 0.70 |
| Densidad de Borde | 85 m/ha | 60 m/ha |

### 6.2 Corredores Ecológicos

Identificación de corredores prioritarios para restauración:

- Corredor Río Aguaytía: 450 ha (conecta fragmentos grandes)
- Corredor Montañoso: 280 ha (conecta con áreas protegidas)
- Corredor Ribereño: 320 ha (protección de cuencas)

## 7. Monitoreo de Carbono con Sensores Remotos

### 7.1 Integración de Datos Multisensor

**Modelo de Estimación de Carbono:**

\`\`\`python
# Modelo de Random Forest para estimación de carbono
from sklearn.ensemble import RandomForestRegressor

features = ['NDVI', 'EVI', 'HV_backscatter', 'HH_backscatter', 
            'elevation', 'slope', 'aspect']

model = RandomForestRegressor(n_estimators=100, random_state=42)
model.fit(X_train, y_train)

# R² = 0.82, RMSE = 15.3 tC/ha
\`\`\`

### 7.2 Validación con Datos de Campo

**Resultados de Validación:**

- Correlación con parcelas de campo: R² = 0.78
- Error absoluto medio: 12.5 tC/ha
- Sesgo: -2.3 tC/ha (subestimación leve)

## 8. Sistema de Alertas Tempranas

### 8.1 Configuración de Alertas

Implementación de sistema automatizado de alertas:

- Frecuencia de análisis: Semanal
- Umbral de detección: Pérdida &gt; 0.5 ha
- Tiempo de respuesta: 48 horas
- Notificación: Email + SMS + Dashboard

### 8.2 Protocolo de Respuesta

**Niveles de Alerta:**

1. **Verde:** Cambios menores (&lt; 0.5 ha) - Monitoreo rutinario
2. **Amarillo:** Cambios moderados (0.5-2 ha) - Verificación en campo en 7 días
3. **Rojo:** Cambios significativos (&gt; 2 ha) - Respuesta inmediata en 48 horas

## 9. Conclusiones del Análisis Geoespacial

### 9.1 Hallazgos Principales

1. El área del proyecto presenta alta heterogeneidad de cobertura terrestre
2. La tasa de deforestación regional (0.8% anual) justifica intervenciones de restauración
3. Los modelos de estimación de biomasa muestran precisión aceptable para monitoreo MRV
4. El sistema de alertas tempranas permite respuesta rápida a amenazas

### 9.2 Recomendaciones Técnicas

- Integrar datos de Sentinel-1 para monitoreo en temporada de lluvias
- Implementar análisis de series temporales con algoritmos de machine learning
- Establecer red de parcelas de validación permanentes
- Desarrollar aplicación móvil para verificación en campo

## 10. Anexos Técnicos

### 10.1 Scripts de Google Earth Engine

Repositorio de código disponible en:
\`https://code.earthengine.google.com/proyecto-afolu-peru\`

### 10.2 Mapas de Referencia

- Mapa de Cobertura Terrestre 2025
- Mapa de Cambio de Cobertura 2015-2025
- Mapa de Biomasa Aérea
- Mapa de Riesgo de Deforestación`;

  const handleSectionClick = (section) => {
    setActiveSection(section?.id);
    const element = document.getElementById(section?.id);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element?.getBoundingClientRect()?.top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleExport = async (format) => {
    console.log(`Exporting report as ${format}`);
    if (format === 'print') {
      window.print();
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['executive-summary', 'project-description', 'methodology', 'mrv-plan', 
                       'carbon-accounting', 'safeguards', 'gee-analysis', 'annexes'];
      
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element?.getBoundingClientRect();
          if (rect?.top >= 0 && rect?.top <= 200) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
      
      <ReportNavigationSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        sections={reportSections}
        activeSection={activeSection}
        onSectionClick={handleSectionClick}
      />

      <main className="lg:ml-64 pt-16">
        <div className="max-w-5xl mx-auto p-6 space-y-6">
          <ReportHeader
            title={reportMetadata?.projectName}
            reportTitle={reportMetadata?.projectName}
            projectId="AFOLU-PERU-001"
            version={reportMetadata?.version}
          />

          <ReportMetadata metadata={reportMetadata} />

          {/* AI-Powered Report Analysis */}
          <ReportAnalysisPanel
            reportData={{
              metadata: reportMetadata,
              content: finalReportContent,
              sections: reportSections
            }}
            reportTitle={reportMetadata?.projectName}
          />

          <ExportControls 
            reportTitle={reportMetadata?.projectName}
            onExport={handleExport}
          />
        </div>
      </main>
    </div>
  );
};

export default ReportRenderingView;